import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';

const db = firebase.firestore();
const functions = firebase.functions();

/**
 * Batch Deactivate Modal - Confirm and execute batch template deactivation
 * Shows aggregate count of open tasks that will be deleted
 */
export function BatchDeactivateModal({ templates, onClose, onSuccess }) {
  const [openTaskCount, setOpenTaskCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    // Count all open tasks for all selected templates (chunked: Firestore `in` max 10)
    const templateIds = templates.map(t => t.id);

    if (templateIds.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const chunks = [];
    for (let i = 0; i < templateIds.length; i += 10) chunks.push(templateIds.slice(i, i + 10));
    Promise.all(chunks.map(ids =>
      db.collection('tasks')
        .where('templateId', 'in', ids)
        .where('status', 'in', ['open', 'overdue'])
        .get()
    ))
      .then(snapshots => {
        if (cancelled) return;
        setOpenTaskCount(snapshots.reduce((n, s) => n + s.size, 0));
        setLoading(false);
      })
      .catch(error => {
        // console statement removed for production
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [templates]);

  const handleConfirm = async () => {
    setConfirming(true);

    try {
      const templateIds = templates.map(t => t.id);
      const batchDeactivateTemplates = functions.httpsCallable('batchDeactivateTemplates');
      const result = await batchDeactivateTemplates({ templateIds });

      onSuccess(result.data);
    } catch (error) {
      // console statement removed for production
      onSuccess({
        successCount: 0,
        failureCount: templates.length,
        totalDeletedTasks: 0,
        failedTemplates: templates.map(t => t.title)
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-danger" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Vorlagen deaktivieren</h2>
          <button className="modal-close" onClick={onClose} disabled={confirming}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="delete-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <div>
            <p>
              <strong>Achtung!</strong> Beim Deaktivieren von {templates.length} Vorlage
              {templates.length !== 1 ? 'n' : ''} werden{' '}
              <strong>
                {loading ? '...' : openTaskCount} offene Aufgabe{openTaskCount !== 1 ? 'n' : ''}
              </strong>{' '}
              gelöscht.
            </p>
            <p>
              Erledigte Aufgaben bleiben als Historie erhalten. Keine neuen Aufgaben werden mehr
              erstellt, bis die Vorlagen wieder aktiviert werden.
            </p>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose} disabled={confirming}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn-warning"
            onClick={handleConfirm}
            disabled={loading || confirming}
          >
            {confirming ? 'Deaktiviere...' : 'Deaktivieren'}
          </button>
        </div>
      </div>
    </div>
  );
}
